import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { LayoutDocument } from '../lib/form-layout/types';
import type { ContractLandFormFieldName } from '../lib/form-layout/contract-land-fields';

export type ContractLandLayoutDocument = LayoutDocument<ContractLandFormFieldName>;

export interface ResolvedLayout {
  layout: ContractLandLayoutDocument;
  scope: 'global' | 'project' | 'default';
  resolved_from: 'project' | 'global' | 'none';
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export const contractLandFormLayoutKeys = {
  all: ['contract-land-form-layout'] as const,
  active: (projectId: string | null) =>
    [...contractLandFormLayoutKeys.all, 'active', projectId] as const,
  adminActive: (scope: 'global' | 'project', projectId: string | null) =>
    [...contractLandFormLayoutKeys.all, 'admin', 'active', scope, projectId] as const,
};

export function useContractLandFormLayout(projectId: string | null) {
  return useQuery({
    queryKey: contractLandFormLayoutKeys.active(projectId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<ResolvedLayout>(
        '/contract-land-form-layout',
        projectId ? { project_id: projectId } : undefined,
      ),
  });
}

export function useAdminActiveContractLandLayout(
  scope: 'global' | 'project',
  projectId: string | null,
) {
  return useQuery({
    queryKey: contractLandFormLayoutKeys.adminActive(scope, projectId),
    queryFn: () => {
      const q: Record<string, string> = { scope };
      if (scope === 'project' && projectId) q.project_id = projectId;
      return api.get<ResolvedLayout>('/admin/contract-land-form-layout/active', q);
    },
    enabled: scope === 'global' || projectId !== null,
  });
}

export interface UpdateContractLandLayoutInput {
  scope: 'global' | 'project';
  project_id?: string | null;
  layout: ContractLandLayoutDocument;
  notes?: string | null;
}

export function useUpdateContractLandFormLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateContractLandLayoutInput) =>
      api.put<ResolvedLayout>('/admin/contract-land-form-layout', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractLandFormLayoutKeys.all }),
  });
}
